/**
 * Text layer for pages that do not have one.
 *
 * Review highlighting works by searching the pdf.js text layer for each extracted value.
 * A scanned PDF has no text layer, so `collectPageSpans` returns nothing and a scan gets
 * no highlights at all — the values were recovered by OCR, but nothing records where on
 * the page they came from.
 *
 * Document processing already knows. It stores a word-box index per page, built from the
 * same pass that produced the text. This fills the empty text layer with those boxes, so
 * every existing consumer — matching, context prioritisation, the highlight overlay,
 * scroll-to-field — works on a scan exactly as it does on a born-digital PDF.
 */

const FILLED_FLAG = 'wordBoxLayer'

// Matches Paperwork::WordBoxes::MAX_BOXES_PER_PAGE. A real page runs to a few hundred
// boxes; a few thousand is a page nobody reviews field by field.
export const MAX_BOXES_PER_PAGE = 5000

/**
 * True when pdf.js rendered a text layer that has no usable text — the scanned case.
 * A page still mid-render is not empty, it is unfinished, so callers should only ask
 * once the layer element exists.
 */
export function needsWordBoxes(pageElement) {
  const textLayer = pageElement?.querySelector('.textLayer')
  if (!textLayer) return false
  if (textLayer.dataset[FILLED_FLAG]) return false

  return textLayer.textContent.trim().length === 0
}

/**
 * Fills one page's text layer from its stored word boxes.
 *
 * Boxes arrive in PDF points with a top-left origin, which is the same convention the
 * rendered page uses, so placing them is a scale rather than a flip. The scale is read
 * from the live element because pdf.js re-renders on zoom.
 *
 * Returns the number of spans added, or 0 if the page was not fillable.
 */
export function fillTextLayer(pageElement, page) {
  const textLayer = pageElement?.querySelector('.textLayer')
  if (!textLayer || !page?.boxes?.length) return 0
  // The server refuses a page this dense too. Held here as well so a stale cached
  // response cannot put tens of thousands of nodes into the document.
  if (page.boxes.length > MAX_BOXES_PER_PAGE) return 0

  const rendered = pageElement.getBoundingClientRect()
  if (!rendered.width || !page.width) return 0

  const scale = rendered.width / page.width
  const fragment = document.createDocumentFragment()

  page.boxes.forEach(box => {
    const span = document.createElement('span')
    span.textContent = box.t
    span.style.position = 'absolute'
    span.style.left = `${box.x * scale}px`
    span.style.top = `${box.y * scale}px`
    span.style.width = `${box.w * scale}px`
    span.style.height = `${box.h * scale}px`
    // The text must not be visible over the rendered page image — it exists to be
    // measured and matched, not read. It stays selectable, which is a small bonus:
    // a scanned page becomes searchable in the browser as a side effect.
    span.style.color = 'transparent'
    span.style.whiteSpace = 'pre'
    span.style.pointerEvents = 'none'
    fragment.appendChild(span)
  })

  textLayer.appendChild(fragment)
  textLayer.dataset[FILLED_FLAG] = 'true'

  return page.boxes.length
}

/**
 * Page numbers whose text layer rendered empty and so need boxes.
 *
 * pdf.js indexes pages from 0 in the DOM and numbers them from 1 in the document.
 *
 * On a born-digital PDF this is always empty and nothing is ever fetched — the whole
 * feature costs those documents one function call. Only scans ask for anything.
 */
export function pagesNeedingWordBoxes(viewerContainer) {
  if (!viewerContainer) return []

  const needed = []

  viewerContainer.querySelectorAll('.page').forEach((pageElement, index) => {
    if (needsWordBoxes(pageElement)) needed.push(index + 1)
  })

  return needed
}

/**
 * Fills the text layers of pages whose boxes have already arrived.
 *
 * `pagesByNumber` is a Map that fills in as fetches land, so this is called repeatedly
 * and skips anything already done.
 */
export function fillAvailableTextLayers(viewerContainer, pagesByNumber) {
  if (!viewerContainer || !pagesByNumber?.size) return 0

  let filled = 0

  viewerContainer.querySelectorAll('.page').forEach((pageElement, index) => {
    if (!needsWordBoxes(pageElement)) return

    const page = pagesByNumber.get(index + 1)
    if (!page || page === 'loading' || page === 'unavailable') return

    filled += fillTextLayer(pageElement, page)
  })

  return filled
}
